import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import StarRateIcon from '@mui/icons-material/StarRate';
import { Chip, CircularProgress, createTheme, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from './ProfileBuilder.module.scss';
import api from '../../api/apiSettings';
import { changeTopSkillAction, deleteSkillAction, restoreAfterRefresh, setNewSkill } from '../../actions/uploadData';
import classes from '../../Components/pages/myProfile/components/scss/skills.module.scss';
import {
  qtyTopSkills,
  minQtyTopSkills,
  maxSymbolQTY,
  validationMessages,
  MOBILE_BREAKPOINT
} from '../../utils/constants';
import { addToSkills } from '../../Components/common/ModalWindows/helpers/helpers';
import ProfileBuilderWrapper from '../../Components/pages/profileBuilder.Wrapper/ProfileBuilderWrapper';
import ValidationErrorLabel from '../../Components/common/ValidationErrorLabel/ValidationErrorLabel';
import { useWindowSize } from '../../utils/hooks';
import colors from '../../scss/colors';

export function ProfileBuilderThree() {
  const dispatch = useDispatch();
  const skills = useSelector((state) => state.uploadData.parsedCV.Skills);
  const topSkills = useSelector((state) => state.uploadData.parsedCV.Skills.filter((skill) => skill?.TopSkill));
  const allCvUploadStore = useSelector((state) => state.uploadData.parsedCV);

  const [currentSkillToAdd, setCurrentSkillToAdd] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([{ label: '', value: 0 }]);
  const [error, setError] = useState('');
  const [skillsFieldTouched, setSkillsFeildTouched] = useState(false);

  const [open, setOpen] = React.useState(false);
  const [t] = useTranslation();
  const size = useWindowSize();
  const validationMessage = useMemo(() => validationMessages(t), [t]);
  const loading = open && suggestions?.length === 0;

  const requestSuggestions = async (value) => {
    let suggestionsResponse = [];

    if (value?.length >= 3) {
      const recievedSuggestions = await api.get(`v1/skills?keyword=${value || ''}`);

      if (recievedSuggestions?.data?.Data.length) {
        suggestionsResponse = recievedSuggestions;
      } else if (value?.length <= maxSymbolQTY.SKILL) {
        suggestionsResponse = {
          data: {
            Data: [{ Name: value, Id: 0 }]
          }
        };
      }
    } else {
      suggestionsResponse = [];
    }

    setSuggestions(
      suggestionsResponse?.data?.Data.map((suggestion) => ({ label: suggestion?.Name, value: suggestion?.Id }))
    );
  };

  const setToSkillsList = (updatedSkillsList) => {
    dispatch(setNewSkill(updatedSkillsList));
    setCurrentSkillToAdd([]);
    setInputValue('');
  };

  const deleteSkill = (skillName) => {
    dispatch(deleteSkillAction(skillName));
  };

  const changeTopSkillProperty = (item) => {
    if (topSkills.length < qtyTopSkills || (topSkills.length === qtyTopSkills && item?.TopSkill))
      dispatch(changeTopSkillAction(item.Name));
  };

  const handleRefresh = () => {
    localStorage.setItem('forRestoreStore', JSON.stringify(allCvUploadStore));
  };

  const restoreData = () => {
    const data = localStorage.getItem('forRestoreStore');

    if (data) {
      dispatch(restoreAfterRefresh({ ...JSON.parse(data) }));
      localStorage.setItem('forRestoreStore', '');
    }
  };

  const validateInitialSkills = () => {
    if (skills.some((skill) => skill.Name.length > maxSymbolQTY.SKILL)) {
      setError(validationMessage.INIT_SKILLS_LONGER_THAN);
    } else {
      setError('');
    }
  };

  useEffect(() => {
    validateInitialSkills();
  }, [skills]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleRefresh);
    restoreData();
    return () => {
      window.removeEventListener('beforeunload', handleRefresh);
    };
  }, [allCvUploadStore.Skills]);

  useEffect(() => {
    let active = true;
    if (!loading) {
      return undefined;
    }
    if (active) {
      requestSuggestions();
    }
    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setSuggestions([]);
    }
  }, [open]);

  const theme = createTheme({
    palette: {
      primary: {
        main: colors.PRIMARY_BLUE
      }
    },
    color: colors.WHITE
  });

  const addHandler = (data) => {
    if (data.length > 0) {
      setToSkillsList(addToSkills(undefined, data[0].label, skills));
      setOpen(false);
    }
  };

  return (
    <ProfileBuilderWrapper
      titleMain={t('profile.builder.start.complete')}
      titleSecondary={t('profile.builder.addskills')}
      text={t('profile.builder.pulledskills')}
      progress={50}
    >
      <div className="row py-4">
        <div className="col-12 text-start">
          <div className={`${styles.outline} p-3 px-4`}>
            {topSkills.length === qtyTopSkills && <span>{validationMessage.TOP_SKILLS_MAX}</span>}
            {topSkills.length < minQtyTopSkills && <span>{validationMessage.TOP_SKILLS_MIN}</span>}
            <hr className="mb-3" />
            <div className={styles.listOfSkills}>
              {skills.map((item) => (
                <ThemeProvider key={item.Name} theme={theme}>
                  <Chip
                    className={classes.chip}
                    label={item.Name}
                    deleteIcon={item?.TopSkill ? <StarRateIcon fontSize="small" /> : <CloseIcon />}
                    color={item.Name.length <= 50 ? 'primary' : 'error'}
                    variant={item?.TopSkill ? 'filled' : 'outlined'}
                    onDelete={() => (!item?.TopSkill ? deleteSkill(item.Name) : changeTopSkillProperty(item))}
                    onClick={() => changeTopSkillProperty(item)}
                    sx={{
                      fontFamily: 'Gotham-Book',
                      height: 'fit-content',
                      paddingY: '4px',
                      '& .MuiChip-deleteIcon': {
                        color: item.TopSkill ? colors.WHITE : colors.PRIMARY_BLUE
                      },
                      '& .MuiChip-label': {
                        whiteSpace: size.width > MOBILE_BREAKPOINT ? 'nowrap' : 'normal'
                      }
                    }}
                  />
                </ThemeProvider>
              ))}
            </div>
            <div className="d-flex flex-row gap-2 align-items-end">
              <div className="d-flex flex-column">
                <Autocomplete
                  multiple
                  value={currentSkillToAdd}
                  inputValue={inputValue}
                  disableCloseOnSelect
                  id="combo-box-demo"
                  open={open}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setOpen(false);
                  }}
                  freeSolo
                  options={suggestions || []}
                  loading={loading}
                  getOptionLabel={(option) => (option.label ? option.label?.toString() : undefined)}
                  onInputChange={(event, value, reason) => {
                    if (reason === 'input') {
                      setSkillsFeildTouched(true);
                      setInputValue(value);
                      requestSuggestions(value);
                    }
                    if (reason === 'clear') {
                      setInputValue('');
                    }
                  }}
                  renderOption={(params, option) => {
                    return (
                      !skills.some((opt) => opt.SkillName === option.label) && (
                        <li {...params} className="d-flex justify-content-between align-items-center ms-3 me-1">
                          {option.label}
                        </li>
                      )
                    );
                  }}
                  renderInput={(params) => (
                    <div className={styles.autocompleteInput}>
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <TextField
                        {...params}
                        label={t('profile.builder.addskills')}
                        variant="standard"
                        onBlur={() => setSkillsFeildTouched(false)}
                        onFocus={() => setSkillsFeildTouched(true)}
                        InputProps={{
                          ...params.InputProps,
                          onKeyDown: (e) => {
                            if (e.key === 'Enter' && params.inputProps.value.length <= maxSymbolQTY.SKILL) {
                              setOpen(false);
                            }
                            e.stopPropagation();
                          },
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                        sx={{ maxWidth: 600 }}
                      />
                    </div>
                  )}
                  onChange={(event, value) => {
                    setCurrentSkillToAdd(value || null);
                    setInputValue('');
                    addHandler(value);
                  }}
                  variant="standard"
                  sx={{
                    display: 'inline-block',
                    minWidth: size.width > MOBILE_BREAKPOINT ? 300 : 200,
                    borderBottom: `1px solid ${colors.PRIMARY_BLUE}`
                  }}
                />
                {inputValue.length < 3 && skillsFieldTouched && (
                  <ValidationErrorLabel message={validationMessage.SKILLS_MIN_CHAR_SEARCH} />
                )}
              </div>
              <ValidationErrorLabel
                color={inputValue.length > maxSymbolQTY.SKILL ? colors.RED_ERROR : colors.BLACK}
                message={`${inputValue.length}/${maxSymbolQTY.SKILL}`}
              />
            </div>
          </div>
        </div>
      </div>
      <ValidationErrorLabel message={error} />
      <div className={`row ${size.width > MOBILE_BREAKPOINT ? 'pt-5' : ''}`}>
        <div className="col-6 text-start">
          <Link
            to="/dashboard/profile-builder-two"
            className="outline-button text-decoration-none p-3 px-5 mt-4"
            title=""
            data-bs-toggle="tooltip"
          >
            <span className="d-inline text-capitalize">{t('common.general.back')}</span>
          </Link>
        </div>
        <div className="col-6 text-end">
          <Link
            to={`${topSkills.length >= minQtyTopSkills && !error ? '/dashboard/profile-builder-four' : ''}`}
            className={`p-3 px-5 mt-4 ${topSkills.length < minQtyTopSkills || error ? 'button-disabled' : 'button'}`}
            title=""
            data-bs-toggle="tooltip"
          >
            <span className="d-inline text-capitalize">{t('common.general.next')}</span>
          </Link>
        </div>
      </div>
    </ProfileBuilderWrapper>
  );
}
